import { callUserApi } from "./callUserApi";
import { 
  UserSignupParams, 
  UserSignupResponse, 
  SendAuthCodeParams, 
  SendAuthCodeResponse,
  VerifyAuthCodeParams,
  VerifyAuthCodeResponse,
  SocialLoginParams,
  SocialLoginResponse,
  SocialProvider,
  LoginParams,
  FindEmailParams,
  SendAuthCodeResetParams,
  ResetPasswordParams,
  ChangePasswordParams,
  ChangePasswordResponse,
  GetTermsParams,
  GetTermsResponse,
  UpdateUserInfoParams,
  ProcessingAuctionsParams,
  ProcessingAuctionsResponse,
  GetAuctionResponse,
  AgreeAuctionResponse,
  GetAgreeAuctionResponse,
  FindAllLotsParams,
  FindAllLotsResponse,
  RecommendedLotsResponse,
  LikeLotResponse,
  GetLikedLotsParams,
  GetLikedLotsResponse,
  PreBidDetailResponse,
  PreBidParams,
  PreBidResponse,
  CancelPreBidResponse,
  GetBidHistoryParams,
  GetBidHistoryResponse,
  GetWonAuctionsParams,
  GetWonAuctionsResponse,
  RefreshTokenParams,
  RefreshTokenResponse,
  GetAuctioneerLotsParams,
  GetAuctioneerLotsResponse,
  GetPushHistoryParams,
  GetPushHistoryResponse,
  UpdatePushSettingsParams,
  UpdatePushSettingsResponse,
  SearchLotsParams,
  SearchLotsResponse,
  DeleteUserResponse,
} from "./userApi.types";

// 환경에 따른 API 호스트 결정
const getApiHost = () => {
  // 브라우저 환경에서 프로토콜 확인
  if (typeof window !== 'undefined') {
    const isHttps = window.location.protocol === 'https:';
    if (!isHttps) {
      // HTTP 환경 (테스트 서버)
      return process.env.REACT_APP_API_DEV_URL;
    } else {
      // HTTPS 환경 (운영 서버)
      return process.env.REACT_APP_API_PROD_URL;
    }
  }
  
  // 서버사이드에서는 기본값 사용
  return process.env.REACT_APP_API_HOST;
};

const API_HOST = getApiHost();

// 🔐 회원가입 (User Registration)

// 회원가입
export async function userSignup(params: UserSignupParams): Promise<{ data: UserSignupResponse; headers?: Headers }> {
  return callUserApi({
    title: "회원가입",
    url: `${API_HOST}/api/users/signup`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 회원가입 시에는 토큰 불필요
  });
}

// 인증번호 전송
export async function sendAuthCode(params: SendAuthCodeParams): Promise<{ data: SendAuthCodeResponse; headers?: Headers }> {
  return callUserApi({
    title: "인증번호 전송",
    url: `${API_HOST}/api/users/email/send-code`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 인증번호 전송 시에는 토큰 불필요
  });
}

// 초기화용 인증번호 전송
export async function sendAuthCodeReset(params: SendAuthCodeResetParams): Promise<{ data: SendAuthCodeResponse; headers?: Headers }> {
  return callUserApi({
    title: "초기화용 인증번호 전송",
    url: `${API_HOST}/api/users/email/find/send-code`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 초기화용 인증번호 전송 시에는 토큰 불필요
  });
}

// 인증번호 검증
export async function verifyAuthCode(params: VerifyAuthCodeParams): Promise<{ data: VerifyAuthCodeResponse; headers?: Headers }> {
  return callUserApi({
    title: "인증번호 검증",
    url: `${API_HOST}/api/users/email/verify`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 인증번호 검증 시에는 토큰 불필요
  });
}

// 일반 로그인
export async function userLogin(params: LoginParams){
  return callUserApi({
    title: "일반 로그인",
    url: `${API_HOST}/api/users/login`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 일반 로그인 시에는 토큰 불필요
  });
}

// 이메일 찾기
export async function findEmail(params: FindEmailParams){
  return callUserApi({
    title: "이메일 찾기",
    url: `${API_HOST}/api/users/email/find`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 이메일 찾기 시에는 토큰 불필요
  });
}

// 비밀번호 초기화
export async function resetPassword(params: ResetPasswordParams): Promise<{ data: any; headers?: Headers }> {
  return callUserApi({
    title: "비밀번호 초기화",
    url: `${API_HOST}/api/users/password/reset`,
    method: "PATCH",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 비밀번호 초기화 시에는 토큰 불필요
  });
}

// 소셜 로그인
export async function socialLogin(
  provider: SocialProvider, 
  params: SocialLoginParams
): Promise<{ data: SocialLoginResponse; headers?: Headers }> {
  return callUserApi({
    title: `${provider} 소셜 로그인`,
    url: `${API_HOST}/api/users/${provider}/login`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 소셜 로그인 시에는 토큰 불필요
  });
}

// 📄 약관 조회 (Terms)

// 약관 조회
export async function getTerms(params?: GetTermsParams): Promise<{ data: GetTermsResponse; headers?: Headers }> {
  const queryParams: { [key: string]: string } = {};
  
  if (params?.termLang) {
    queryParams.termLang = params.termLang;
  }

  const queryString = Object.keys(queryParams).length > 0 
    ? '?' + new URLSearchParams(queryParams).toString() 
    : '';

  return callUserApi({
    title: "약관 조회",
    url: `${API_HOST}/api/terms${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: false, // 약관 조회 시에는 토큰 불필요
  });
}

// 👤 사용자 정보 (User Info)

// 사용자 정보 조회
export async function getUserInfo(): Promise<{ data: any; headers?: Headers }> {
  return callUserApi({
    title: "사용자 정보 조회",
    url: `${API_HOST}/api/users/info`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 사용자 정보 조회 시에는 토큰 필요
  });
}

// 비밀번호 변경
export async function changePassword(params: ChangePasswordParams): Promise<{ data: ChangePasswordResponse; headers?: Headers }> {
  return callUserApi({
    title: "비밀번호 변경",
    url: `${API_HOST}/api/users/info/password`,
    method: "PATCH",
    body: params,
    isCallPageLoader: true,
    withToken: true, // 비밀번호 변경 시에는 토큰 필요
  });
}

// 이메일 중복 확인
export async function checkEmailExist(email: string): Promise<{ data: any; headers?: Headers }> {
  return callUserApi({
    title: "이메일 중복 확인",
    url: `${API_HOST}/api/users/email/check`,
    method: "POST",
    body: { email },
    isCallPageLoader: true,
    withToken: false,
  });
}

// 사용자 정보 수정
export async function updateUserInfo(params: UpdateUserInfoParams): Promise<{ data: any; headers?: Headers }> {
  return callUserApi({
    title: "사용자 정보 수정",
    url: `${API_HOST}/api/users/info`,
    method: "PATCH",
    body: params,
    isCallPageLoader: true,
    withToken: true,
  });
}

// 회원탈퇴
export async function deleteUser(): Promise<{ data: DeleteUserResponse; headers?: Headers }> {
  return callUserApi({
    title: "회원탈퇴",
    url: `${API_HOST}/api/users`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true, // 회원탈퇴는 로그인이 필요
  });
}

// 🏛️ 경매 관련 API (Auction APIs)

// 진행 중인 경매 조회
export async function getProcessingAuctions(params?: ProcessingAuctionsParams): Promise<{ data: ProcessingAuctionsResponse; headers?: Headers }> {
  return callUserApi({
    title: "진행 중인 경매 조회",
    url: `${API_HOST}/api/auctions/processing`,
    method: "GET",
    params: params ? { page: params.page?.toString() } : undefined,
    isCallPageLoader: true,
    withToken: false,
  });
}

// 경매 조회
export async function getAuction(index: string | number): Promise<{ data: GetAuctionResponse; headers?: Headers }> {
  return callUserApi({
    title: "경매 조회",
    url: `${API_HOST}/api/auctions/${index}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: false,
  });
}

// 경매 동의
export async function agreeAuction(index: string | number): Promise<{ data: AgreeAuctionResponse; headers?: Headers }> {
  return callUserApi({
    title: "경매 동의",
    url: `${API_HOST}/api/auctions/${index}/agree`,
    method: "POST",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 경매 동의 여부 조회
export async function getAgreeAuction(index: string | number): Promise<{ data: GetAgreeAuctionResponse; headers?: Headers }> {
  return callUserApi({
    title: "경매 동의 여부 조회",
    url: `${API_HOST}/api/auctions/${index}/agree`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true,
  });
}

// 🎨 LOT 관련 API (Lot APIs)

// 추천 LOT 조회
export async function getRecommendedLots(): Promise<{ data: RecommendedLotsResponse; headers?: Headers }> {
  return callUserApi({
    title: "추천 LOT 조회",
    url: `${API_HOST}/api/lots/recommended`,
    method: "GET",
    isCallPageLoader: true,
    withToken: false,
  });
}

// LOT 검색 (새로운 API)
export async function searchLots(params?: SearchLotsParams): Promise<{ data: SearchLotsResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {};
  
  // categoryCode가 'all'이 아닌 경우에만 추가
  if (params?.categoryCode && params.categoryCode !== 'all') {
    queryParams.categoryCode = params.categoryCode;
  }

  // keyword 추가
  if (params?.keyword && params.keyword.trim() !== '') {
    queryParams.keyword = params.keyword.trim();
  }
  
  // auctionStatus 추가
  if (params?.auctionStatus) {
    queryParams.auctionStatus = params.auctionStatus;
  }
  
  // order 추가
  if (params?.order) {
    queryParams.order = params.order;
  }
  
  // user 추가
  if (params?.user) {
    queryParams.user = params.user.toString();
  }
  
  // page 추가 (기본값: 1)
  queryParams.page = (params?.page || 1).toString();
  
  // limit 추가 (기본값: 30)
  queryParams.limit = (params?.limit || 30).toString();

  const queryString = new URLSearchParams(queryParams).toString();

  return callUserApi({
    title: "LOT 검색",
    url: `${API_HOST}/api/lots/search?${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: false,
  });
}

// 모든 LOT 조회
export async function getAllLots(params: FindAllLotsParams): Promise<{ data: FindAllLotsResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {
    isActive: params.isActive.toString(),
    page: params.page.toString(),
  };

  if (params.subCategoryCode) {
    if (Array.isArray(params.subCategoryCode)) {
      queryParams.subCategoryCode = params.subCategoryCode.join(',');
    } else {
      queryParams.subCategoryCode = params.subCategoryCode;
    }
  }

  if (params.price) {
    queryParams.price = params.price;
  }

  return callUserApi({
    title: "모든 LOT 조회",
    url: `${API_HOST}/api/lots`,
    method: "GET",
    params: queryParams,
    isCallPageLoader: true,
    withToken: false,
  });
}

// LOT 좋아요/좋아요 취소
export async function likeLot(id: string | number): Promise<{ data: LikeLotResponse; headers?: Headers }> {
  return callUserApi({
    title: "LOT 좋아요",
    url: `${API_HOST}/api/users/lots/${id}/like`,
    method: "POST",
    isCallPageLoader: true,
    withToken: true, // 좋아요 기능은 로그인이 필요
  });
}

// 좋아요한 작품 조회
export async function getLikedLots(params?: GetLikedLotsParams): Promise<{ data: GetLikedLotsResponse; headers?: Headers }> {
  return callUserApi({
    title: "좋아요한 작품 조회",
    url: `${API_HOST}/api/users/lots/liked`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 좋아요한 작품 조회는 로그인이 필요
  });
}

// 사전입찰 상세조회
export async function getPreBidDetail(id: string | number): Promise<{ data: PreBidDetailResponse; headers?: Headers }> {
  return callUserApi({
    title: "사전입찰 상세조회",
    url: `${API_HOST}/api/users/lots/${id}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 사전입찰 상세조회는 로그인이 필요
  });
}

// 사전 입찰하기
export async function preBid(id: string | number, params: PreBidParams): Promise<{ data: PreBidResponse; headers?: Headers }> {
  return callUserApi({
    title: "사전 입찰하기",
    url: `${API_HOST}/api/users/lots/${id}/pre-bid`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: true, // 사전 입찰하기는 로그인이 필요
  });
}

// 사전 입찰 취소
export async function cancelPreBid(id: string | number): Promise<{ data: CancelPreBidResponse; headers?: Headers }> {
  return callUserApi({
    title: "사전 입찰 취소",
    url: `${API_HOST}/api/users/lots/${id}/pre-bid`,
    method: "DELETE",
    isCallPageLoader: true,
    withToken: true, // 사전 입찰 취소는 로그인이 필요
  });
}

// 입찰내역조회
export async function getBidHistory(params?: GetBidHistoryParams): Promise<{ data: GetBidHistoryResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {};
  
  if (params?.page) {
    queryParams.page = params.page.toString();
  }
  
  if (params?.limit) {
    queryParams.limit = params.limit.toString();
  }

  if (params?.status) {
    queryParams.status = params.status;
  }

  const queryString = Object.keys(queryParams).length > 0 
    ? '?' + new URLSearchParams(queryParams).toString() 
    : '';

  return callUserApi({
    title: "입찰내역조회",
    url: `${API_HOST}/api/users/bids${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 입찰내역조회는 로그인이 필요
  });
}

// 낙찰경매조회
export async function getWonAuctions(params?: GetWonAuctionsParams): Promise<{ data: GetWonAuctionsResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {};
  
  if (params?.page) {
    queryParams.page = params.page.toString();
  }
  
  if (params?.limit) {
    queryParams.limit = params.limit.toString();
  }
  
  if (params?.status) {
    queryParams.status = params.status;
  }

  const queryString = Object.keys(queryParams).length > 0 
    ? '?' + new URLSearchParams(queryParams).toString() 
    : '';

  return callUserApi({
    title: "낙찰경매조회",
    url: `${API_HOST}/api/users/lots/won${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 낙찰경매조회는 로그인이 필요
  });
}
// 🔄 리프레시 토큰 재발급
export async function refreshToken(params: RefreshTokenParams): Promise<{ data: RefreshTokenResponse; headers?: Headers }> {
  return callUserApi({
    title: "리프레시 토큰 재발급",
    url: `${API_HOST}/api/users/refresh`,
    method: "POST",
    body: params,
    isCallPageLoader: true,
    withToken: false, // 액세스 토큰 사용 안함
    withRefreshToken: true, // 리프레시 토큰을 헤더에 전달
  });
}

// 🎨 경매사별 작품 조회 API
export async function getAuctioneerLots(params: GetAuctioneerLotsParams): Promise<{ data: GetAuctioneerLotsResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {
    page: (params.page || 1).toString(),
  };

  if (params.order) {
    queryParams.order = params.order;
  }

  const queryString = new URLSearchParams(queryParams).toString();

  return callUserApi({
    title: "경매사별 작품 조회",
    url: `${API_HOST}/api/users/auctioneers/${params.id}/lots?${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: false, // 경매사별 작품 조회는 토큰 불필요
  });
}

// 📱 푸쉬 내역 조회 API
export async function getPushHistory(params?: GetPushHistoryParams): Promise<{ data: GetPushHistoryResponse; headers?: Headers }> {
  const queryParams: Record<string, string> = {
    page: (params?.page || 1).toString(),
    limit: (params?.limit || 30).toString(),
  };

  const queryString = new URLSearchParams(queryParams).toString();

  return callUserApi({
    title: "푸쉬 내역 조회",
    url: `${API_HOST}/api/users/push?${queryString}`,
    method: "GET",
    isCallPageLoader: true,
    withToken: true, // 푸쉬 내역 조회는 로그인이 필요
  });
}

// 📱 푸쉬 알림 설정 수정 API
export async function updatePushSettings(params: UpdatePushSettingsParams): Promise<{ data: UpdatePushSettingsResponse; headers?: Headers }> {
  return callUserApi({
    title: "푸쉬 알림 설정 수정",
    url: `${API_HOST}/api/users/push`,
    method: "PATCH",
    body: params,
    isCallPageLoader: true,
    withToken: true, // 푸쉬 알림 설정 수정은 로그인이 필요
  });
}
